import {withSuspenseLoader} from '@yandex-delivery/blocks'
import {DrawerOverlay, DrawerSection} from '@yandex-delivery/blocks/legacy'
import clsx from 'clsx'
import React, {lazy, useMemo} from 'react'
import type {SharedRouteInfoResponse} from 'src/api/types'
import {RouteExperiments, config, isMarketIntegration} from 'src/shared/config'
import {Brand} from 'src/shared/types'
import {isIos, isSellAndBuy} from 'src/shared/utils'

import {useIsMobile, useSharedRoutePerformer} from '../../hooks'
import {MapModes} from '../../types'
import {getMapProps} from '../../utils'
import {AccentButton} from '../accent-button'
import {OpenRoverButton} from '../action-open-rover'
import {AdsBanner} from '../ads-banner'
import {BottomContentSections, Content} from '../content'
import {DrawerDesktop, DrawerMobile, DrawerSection as DrawerSectionDesktop} from '../drawer'
import {Feedback} from '../feedback'
import {Header} from '../header'
// biome-ignore lint/suspicious/noShadowRestrictedNames: компонент карты, глобальный Map в модуле не нужен
import {Map} from '../map'
import {Payment} from '../payment'
import {Poll} from '../poll'
import {PostcardDesktop, PostcardMobile} from '../postcard'
import {DesktopPromoContainer, MobilePromoContainer, type PromoProps, YandexPromo, YangoPromo} from '../promo'
import {PromoBanner as ExperimentalPromoBanner} from '../promo-banner'
import {ServiceCloseButton} from '../service-close-button'
import {Status} from '../status'
import {Timeline} from '../timeline'
import {classes} from './ActiveScreen.classes'
import {isBannerPositionUp, isCountry, toActionsPayload} from './ActiveScreen.utils'

const Actions = withSuspenseLoader(lazy(() => import('../actions')))
const AutoBanner = withSuspenseLoader(lazy(() => import('../auto-banner')))
const WebTips = withSuspenseLoader(lazy(() => import('../web-tips')))

type Props = {
  info: SharedRouteInfoResponse
}

const ActiveScreen: React.FC<Props> = ({info}) => {
  const Promo: React.FC<PromoProps> = config.brand === Brand.Yango ? YangoPromo : YandexPromo
  const performer = useSharedRoutePerformer()
  const {mapProps, headActions, paymentAction, webTipsAction, footActions, isShowFeedback, openRoverActions} =
    useMemo(() => {
      return Object.assign(toActionsPayload(info), {
        mapProps: getMapProps(info, performer),
      })
    }, [info, performer])
  const isMobile = useIsMobile()
  const isNotTurkey = !isCountry('tr')

  // Вероятность того что в info изменится content_sections мала,
  // а если и изменится, то toString сработает и обновит,
  // но зато мы избегаем лишних перерисовок при поллинге, из-за которого еще доп запросы были к иконкам.
  // biome-ignore lint/correctness/useExhaustiveDependencies: мемоизация намеренно по значению, а не по ссылке
  const contentSections = useMemo(() => info.content_sections, [info.content_sections?.toString()])

  const {DrawerComponent, DrawerSectionComponent, Postcard} = useMemo(() => {
    if (isMobile) {
      return {
        DrawerComponent: DrawerMobile,
        DrawerSectionComponent: DrawerSection,
        Postcard: PostcardMobile,
      }
    }

    return {
      DrawerComponent: DrawerDesktop,
      DrawerSectionComponent: DrawerSectionDesktop,
      Postcard: PostcardDesktop,
    }
  }, [isMobile])

  const isMobileBannerShown = isMobile && !info.postcard
  const isExperimentalPromoBannerShown = isBannerPositionUp(info)
  const deeplink = info.promo_banner?.trail_button?.deeplink || info.promo_banner?.lead_button?.deeplink
  const isSellAndBuyAvailable = isSellAndBuy()
  const experiments = config.experiments3
  const isAdsBannerEnabled = experiments?.[RouteExperiments.AdsBanner]?.enabled ?? false
  const isIosDevice = isIos()

  const pointACoordinates = info.route_points?.[0]?.coordinates
  const pointAGps = pointACoordinates != null ? `${pointACoordinates[1]},${pointACoordinates[0]}` : undefined

  return (
    /**
     * Пора переходить на новый дровер - CARGODEV-34815
     */
    <DrawerOverlay zIndex={950}>
      {/* Карте становится плохо при RTL, маршрут съезжает */}
      <div dir="ltr">
        <Map {...mapProps} />
      </div>
      <Header variant={info.postcard ? 'postcard' : undefined} />
      <DrawerComponent>
        {isMobile && <AutoBanner />}
        {!!info.poll && (
          <div className={classes.drawerSection}>
            <DrawerSectionComponent>
              <Poll poll={info.poll} />
            </DrawerSectionComponent>
          </div>
        )}
        <div className={clsx(classes.drawerSection, classes.content)}>
          <DrawerSectionComponent>
            <Status info={info} />
            {info.timeline && <Timeline timeline={info.timeline} />}
            {isMobile &&
              openRoverActions.length > 0 &&
              openRoverActions.map((item, index) => <OpenRoverButton action={item} key={`open-rover-btn-${index}`} />)}
            {isShowFeedback && mapProps.mode === MapModes.Delivered && <Feedback />}
            {webTipsAction && <WebTips webTipsAction={webTipsAction} />}
            {headActions.length > 0 && <Actions actions={headActions} />}
            {isAdsBannerEnabled && (
              <div className={classes.bannerContainer}>
                <AdsBanner gps={pointAGps} isMobile={isMobile} />
              </div>
            )}
          </DrawerSectionComponent>
          {paymentAction && (
            <DrawerSectionComponent>
              <Payment paymentAction={paymentAction} />
            </DrawerSectionComponent>
          )}
          {info.accent_state_button && (
            <DrawerSectionComponent>
              <AccentButton button={info.accent_state_button} />
            </DrawerSectionComponent>
          )}
          {isMobileBannerShown && !isExperimentalPromoBannerShown && !isMarketIntegration && !isIosDevice && (
            <DrawerSectionComponent>
              {isNotTurkey ? (
                <div className={classes.promoWrapper}>
                  {!isSellAndBuyAvailable && <Promo deeplink={deeplink} fullWidth />}
                </div>
              ) : null}
            </DrawerSectionComponent>
          )}

          {contentSections && <Content sections={contentSections} />}
          {footActions.length > 0 && (
            <DrawerSectionComponent fullHeight>
              <Actions actions={footActions} />
            </DrawerSectionComponent>
          )}
        </div>
        {info.bottom_content_sections && (
          <BottomContentSections floating={isMobile} sections={info.bottom_content_sections} />
        )}
      </DrawerComponent>

      {info.postcard && <Postcard data={info.postcard} />}

      {/**
       * TODO: Подумать. Сейчас не понятно, почему контейнер под промо лежит рядом с промо.
       * Отвечает за положение на этом экране. Сейчас сделал по аналогии с тем, что есть
       */}
      {isMobileBannerShown &&
        isExperimentalPromoBannerShown &&
        !isMarketIntegration &&
        !isSellAndBuyAvailable &&
        !isIosDevice && (
          <MobilePromoContainer>
            <ExperimentalPromoBanner {...info.promo_banner} />
          </MobilePromoContainer>
        )}

      {/* Кнопка отображается в вебвью для проекта Sell&Buy, см. https://nda.ya.ru/t/H8o4k4li79xnSK */}
      {isMobile && isSellAndBuyAvailable && <ServiceCloseButton />}

      {!isMobileBannerShown && isNotTurkey && !isMarketIntegration && !isIosDevice ? (
        <DesktopPromoContainer>
          {!isSellAndBuyAvailable && <Promo deeplink={deeplink} />}
          {!isMobile && <AutoBanner />}
        </DesktopPromoContainer>
      ) : null}
    </DrawerOverlay>
  )
}

export default ActiveScreen
